import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Purchaser } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import useSessionUser from '@/modules/access/hooks/useSessionUser'
import useLazyGetOrganisationChildren from '@/modules/admin-center/hooks/useLazyGetOrganisationChildren'
import { NestedPurchaserRow } from '@/modules/admin-center/organisation-hierarchy/NestedPurchaserRow'
import { Button } from '@/modules/shared/components/button/Button'
import { QueryResult } from '@/modules/shared/components/query-result/QueryResult'
import { Tooltip } from '@/modules/shared/components/tooltip/Tooltip'
import { useViewType } from '@/modules/shared/hooks/useViewType'
import { useWindowSize } from '@/modules/shared/hooks/useWindowSize'
import { ChevronDownIcon } from '@/modules/shared/icons/ChevronDownIcon'
import { classNames } from '@/modules/shared/utils/classNames'

interface OrganisationHierarchyProps {
  organisationId?: number
  isHeader?: boolean
  openByDefault?: boolean
}

export default function OrganisationHierarchy({
  organisationId,
  isHeader = false,
  openByDefault = false,
}: OrganisationHierarchyProps) {
  const { t } = useTranslation()
  const [showOrgHierarchy, setShowOrgHierarchy] = useState(openByDefault)
  const { currentOrganisation } = useSessionUser()
  const { fetchOrganisationChildren, organisation, loadingStates, error } = useLazyGetOrganisationChildren({
    organisationId: organisationId || Number(currentOrganisation?.id),
  })
  const navigate = useNavigate()
  const { twoXL } = useWindowSize()
  const { isUtility } = useViewType()

  useEffect(() => {
    if (showOrgHierarchy) {
      fetchOrganisationChildren()
    }
  }, [showOrgHierarchy])

  return (
    <section className="rounded-md bg-white text-sm shadow">
      {isHeader && (
        <div className="flex min-h-[60px] items-center justify-between px-4 py-3">
          <div className="flex items-center gap-x-2 font-bold">
            {t('general.organisationHierarchy', 'Organisation Hierarchy')}
          </div>
          <div className="flex items-center gap-x-2">
            {isUtility && (
              <Button
                className="rounded-md bg-gray-200 px-4 py-2"
                onClick={() =>
                  navigate(
                    `/utilities/organisations/${organisation?.isAPurchaser ? 'purchasers' : 'suppliers'}/create?parentId=${organisationId}&country=${organisation?.postalAddressCountry}`
                  )
                }
                data-testid="add-org-modal-button"
              >
                {t('general.addOrganisation', 'Add Organisation')}
              </Button>
            )}
            <ChevronDownIcon
              className={classNames(
                'size-8 cursor-pointer rounded-full p-1 text-gray-500 transition hover:bg-gray-100',
                {
                  '-rotate-180 transform': showOrgHierarchy,
                }
              )}
              onClick={() => setShowOrgHierarchy(!showOrgHierarchy)}
              data-testid="show-org-hierarchy-panel"
            />
          </div>
        </div>
      )}
      {(showOrgHierarchy || !isHeader) && (
        <>
          <hr />
          <QueryResult loading={loadingStates.loading} error={error} spinnerClassName="pb-12">
            <div className="overflow-x-scroll p-4">
              <table className="w-full min-w-[60rem] bg-white text-left text-sm" data-testid="org-hierarchy-table">
                <thead className="text-xs text-gray-500">
                  <tr>
                    <th className="w-full whitespace-nowrap">
                      <Tooltip content={t('orgHierarchy.organisationNameTooltip', 'The name of the Organisation.')}>
                        <div className="w-min rounded bg-gray-100 px-3 py-1">
                          {t('general.organisationName', 'Organisation Name')}
                        </div>
                      </Tooltip>
                    </th>
                    {organisation?.featureFlags?.newApprovalWorkflows && organisation?.isAPurchaser && (
                      <th className="w-full whitespace-nowrap">
                        <Tooltip
                          content={t(
                            'orgHierarchy.invoiceWorkflowTooltip',
                            'The Invoice Approval Workflow for the Organisation.'
                          )}
                        >
                          <div className="w-min rounded bg-gray-100 px-3 py-1">
                            {t('general.invoiceApprovalWorkflow', 'Invoice Approval Workflow')}
                          </div>
                        </Tooltip>
                      </th>
                    )}
                    <th className="min-w-32 whitespace-nowrap">
                      <div className="flex justify-center">
                        <Tooltip
                          content={t(
                            'orgHierarchy.orgIdTooltip',
                            'The unique identifier for the Organisation in PurchasePlus.'
                          )}
                        >
                          <div className="w-min rounded bg-gray-100 px-3 py-1">{t('general.orgId', 'Org ID')}</div>
                        </Tooltip>
                      </div>
                    </th>
                    <th className="min-w-24 whitespace-nowrap">
                      <div className="flex justify-center">
                        <Tooltip
                          content={t(
                            'orgHierarchy.legalEntityTooltip',
                            'Typically a Legal Entity is an official business in the real world, while a Non-Legal Entity is a department beneath a Legal Entity.'
                          )}
                        >
                          <div className="w-min rounded bg-gray-100 px-3 py-1">
                            {t('general.legalEntity', 'Legal Entity')}
                          </div>
                        </Tooltip>
                      </div>
                    </th>
                    <></>
                    {isUtility ? (
                      <th className="min-w-24 whitespace-nowrap">
                        <div className="flex justify-center">
                          <Tooltip
                            content={t(
                              'orgHierarchy.isVerifiedTooltip',
                              'Typically a Verified Organisation is an official user of PurchasePlus, who operates their own account while a Non-Verified Organisation typically applies to Supplier Organisations only, where the Supplier is to be managed by a Purchaser.'
                            )}
                          >
                            <div className="w-min rounded bg-gray-100 px-3 py-1">
                              {t('general.isVerified', 'Is Verified')}
                            </div>
                          </Tooltip>
                        </div>
                      </th>
                    ) : (
                      <>
                        {twoXL && organisation?.isAPurchaser && (
                          <>
                            <th className="min-w-44 whitespace-nowrap">
                              <div className="flex justify-center">
                                <div className="w-min rounded bg-gray-100 px-3 py-1">
                                  {t('general.costCenterCode', 'Cost Center Code')}
                                </div>
                              </div>
                            </th>
                            <th className="min-w-48 whitespace-nowrap">
                              <div className="flex justify-center">
                                <div className="w-min rounded bg-gray-100 px-3 py-1">
                                  {t('general.defaultAccount', 'Default Account')}
                                </div>
                              </div>
                            </th>
                          </>
                        )}
                      </>
                    )}
                    {organisation?.isAPurchaser && (
                      <th className="min-w-24 whitespace-nowrap">
                        <div className="flex justify-center">
                          <div className="w-min rounded bg-gray-100 px-3 py-1">{t('general.info', 'Info')}</div>
                        </div>
                      </th>
                    )}
                    <th className="min-w-24 whitespace-nowrap">
                      <div className="flex justify-center">
                        <div className="w-min rounded bg-gray-100 px-3 py-1">
                          {isUtility ? t('general.manage', 'Manage') : t('general.select', 'Select')}
                        </div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {organisation && <NestedPurchaserRow organisation={organisation as Purchaser} level={0} />}
                </tbody>
              </table>
            </div>
          </QueryResult>
        </>
      )}
    </section>
  )
}
